// src/pages/Magasins.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import { getActiveToken, getActiveAuthRole } from '../utils/authStorage';
import {
  FaPlus,
  FaStore,
  FaCircle,
  FaTimes,
  FaSpinner,
  FaSync,
  FaTrash,
  FaEdit,
  FaToggleOn,
  FaToggleOff,
  FaChevronLeft,
  FaChevronRight,
  FaSearch,
  FaArrowLeft,
  FaMapMarkerAlt,
  FaUserTie
} from 'react-icons/fa';

const STORE_API = process.env.REACT_APP_STORE_SERVICE_URL || 'http://localhost:3006';
const AUTH_API = process.env.REACT_APP_AUTH_SERVICE_URL || 'http://localhost:3000';

/** Tri alphabétique sur le nom (secours : code) pour affichage cohérent. */
function sortStoresByName(list) {
  if (!Array.isArray(list)) return [];
  return list.slice().sort((a, b) => {
    const byName = String(a?.name || '').localeCompare(String(b?.name || ''), 'fr', { sensitivity: 'base' });
    if (byName !== 0) return byName;
    return String(a?.code || '').localeCompare(String(b?.code || ''), 'fr', { sensitivity: 'base' });
  });
}

const formAccentBtnClass = 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg hover:from-blue-700 hover:to-blue-800 transition-all';
const formInputClass = 'w-full rounded-xl border border-slate-200/90 bg-slate-50/50 px-3.5 py-2.5 text-sm text-slate-800 shadow-[inset_0_1px_2px_rgba(15,23,42,0.03)] transition duration-200 placeholder:text-slate-400 hover:border-slate-300 hover:bg-white focus:border-blue-600 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600/15';
const formLabelClass = 'mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400';

function StoreFormDivider({ label }) {
  return (
    <div className="col-span-full flex items-center gap-3 pt-1">
      <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.14em] text-blue-700">{label}</span>
      <div className="h-px flex-1 bg-slate-200" />
    </div>
  );
}

function StoreFormField({ label, required, className = '', children }) {
  return (
    <div className={className}>
      <label className={formLabelClass}>
        {label}{required ? <span className="text-blue-600"> *</span> : ''}
      </label>
      {children}
    </div>
  );
}

function StoreFormShell({
  title,
  subtitle,
  formId,
  onSubmit,
  onBack,
  onCancel,
  submitting,
  submitLabel,
  SubmitIcon,
  children,
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="shrink-0 border-b bg-white shadow-sm">
        <div className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between sm:p-4 lg:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-r from-blue-600 to-blue-700">
              <FaStore className="text-sm text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="truncate text-lg font-bold text-gray-900 sm:text-xl">{title}</h1>
              {subtitle && <p className="truncate text-xs text-gray-500">{subtitle}</p>}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onBack}
              disabled={submitting}
              className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 sm:text-sm"
            >
              <FaArrowLeft className="text-xs" />
              Retour
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="rounded-lg border px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 sm:text-sm"
            >
              Annuler
            </button>
            <button
              type="submit"
              form={formId}
              disabled={submitting}
              className={`inline-flex items-center rounded-lg px-3 py-2 text-xs font-medium disabled:opacity-50 sm:text-sm ${formAccentBtnClass}`}
            >
              {submitting ? <FaSpinner className="mr-1.5 animate-spin" /> : SubmitIcon ? <SubmitIcon className="mr-1.5 text-xs" /> : null}
              {submitLabel}
            </button>
          </div>
        </div>
      </div>

      <form id={formId} onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="min-h-0 flex-1 overflow-hidden p-3 sm:p-4 lg:p-6">
          <div className="h-full rounded-xl border border-slate-200/80 bg-white p-5 shadow-sm ring-1 ring-slate-900/5 sm:p-6 lg:p-8">
            {children}
          </div>
        </div>
      </form>
    </div>
  );
}

export default function Magasins() {
  const navigate = useNavigate();
  const userRole = getActiveAuthRole();
  const isAdmin = userRole === 'admin';
  const isMerchant = userRole === 'merchant';
  const canManageStores = isAdmin;

  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // kept for compatibility if you still need to fetch users elsewhere
  const [users, setUsers] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [merchantsLoading, setMerchantsLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [isUpdatingId, setIsUpdatingId] = useState(null);
  const [editingStoreId, setEditingStoreId] = useState(null);

  const [newStore, setNewStore] = useState({
    name: '',
    code: '',
    address: { street: '', city: '', postalCode: '', country: 'Tunisie' },
    phone: '',
    email: '',
    description: '',
    merchantId: ''
  });
  const [editStore, setEditStore] = useState({
    name: '',
    code: '',
    address: { street: '', city: '', postalCode: '', country: 'Tunisie' },
    phone: '',
    email: '',
    description: '',
    merchantId: ''
  });

  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  const [draftName, setDraftName] = useState('');
  const [draftCode, setDraftCode] = useState('');
  const [appliedName, setAppliedName] = useState('');
  const [appliedCode, setAppliedCode] = useState('');

  // auth helpers
  const getToken = () => getActiveToken() || null;
  const authHeaders = () => {
    const token = getToken();
    const base = { 'Content-Type': 'application/json' };
    if (token) return { ...base, Authorization: `Bearer ${token}` };
    return base;
  };

  const handleAuthError = async (res) => {
    let body = null;
    try { body = await res.json(); } catch (e) { /* ignore */ }
    const msg = (body && body.message) ? body.message.toLowerCase() : '';
    if (res.status === 401 || msg.includes('token') || msg.includes('authorization') || msg.includes('non autorisé') || msg.includes('non autorise')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      navigate('/login');
      throw new Error(body?.message || 'Authentification requise');
    }
    throw new Error(body?.message || `Erreur ${res.status}`);
  };

  // --- Fetch stores
  const fetchStores = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = authHeaders();
      if (!headers) { navigate('/login'); return; }
      const res = await fetch(`${STORE_API.replace(/\/$/, '')}/stores`, { headers });
      if (!res.ok) await handleAuthError(res);
      const data = await res.json();

      // Support responses like: { success: true, stores: [...] } or directly an array
      const list = Array.isArray(data.stores) ? data.stores : (Array.isArray(data) ? data : (data.stores || []));

      const sorted = sortStoresByName(list);

      setStores(sorted);
      setStats({
        total: sorted.length,
        active: sorted.filter(s => s.active === true).length,
        inactive: sorted.filter(s => s.active === false).length
      });
    } catch (err) {
      console.error('fetchStores error', err);
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  };

  // optional: still fetch users only if you need them elsewhere
  const fetchUsers = async () => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const headers = authHeaders();
      if (!headers) { navigate('/login'); return; }
      const res = await fetch(`${AUTH_API.replace(/\/$/, '')}/users`, { headers });
      if (!res.ok) await handleAuthError(res);
      const data = await res.json();
      const list = Array.isArray(data) ? data : (Array.isArray(data.users) ? data.users : []);
      setUsers(list);
    } catch (err) {
      console.error('fetchUsers error', err);
      setUsersError(err.message || String(err));
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchMerchants = async () => {
    if (!isAdmin) return;
    setMerchantsLoading(true);
    try {
      const headers = authHeaders();
      if (!headers) return;
      const res = await fetch(`${AUTH_API.replace(/\/$/, '')}/merchants`, { headers });
      if (!res.ok) return;
      const data = await res.json();
      const list = Array.isArray(data.merchants) ? data.merchants : (Array.isArray(data) ? data : []);
      const merchantOptions = list
        .map((m) => {
          const id = m?.id || m?._id;
          const name =
            m?.userFullName ||
            `${m?.user?.name || m?.userName || ''} ${m?.user?.lastName || m?.userLastName || ''}`.trim() ||
            m?.email ||
            String(id);
          return id ? { id: String(id), name } : null;
        })
        .filter(Boolean);
      setMerchants(merchantOptions);
    } catch (err) {
      console.error('fetchMerchants error', err);
      setMerchants([]);
    } finally {
      setMerchantsLoading(false);
    }
  };

  useEffect(() => {
    fetchStores();
    fetchUsers();
    fetchMerchants();
  }, []);

  const filteredStores = stores.filter((store) => {
    const nameNeedle = appliedName.trim().toLowerCase();
    const codeNeedle = appliedCode.trim().toLowerCase();
    if (nameNeedle && !String(store?.name || '').toLowerCase().includes(nameNeedle)) return false;
    if (codeNeedle && !String(store?.code || '').toLowerCase().includes(codeNeedle)) return false;
    return true;
  });

  const totalStores = filteredStores.length;
  const totalPages = totalStores === 0 ? 0 : Math.ceil(totalStores / itemsPerPage);

  useEffect(() => {
    if (totalPages === 0) return;
    setCurrentPage((p) => (p > totalPages ? totalPages : p));
  }, [totalPages]);

  const safeCurrentPage = totalPages === 0 ? 1 : Math.min(Math.max(1, currentPage), totalPages);
  const startIndex = (safeCurrentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalStores);
  const paginatedStores = filteredStores.slice(startIndex, startIndex + itemsPerPage);
  const tableColSpan = canManageStores ? (isAdmin ? 7 : 6) : (isAdmin ? 6 : 5);

  const getStatusBadge = (active) => (
    active ? (
      <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200/70">
        <FaCircle className="mr-1 h-2 w-2" />Actif
      </span>
    ) : (
      <span className="inline-flex items-center rounded-full bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700 ring-1 ring-rose-200/70">
        <FaCircle className="mr-1 h-2 w-2" />Inactif
      </span>
    )
  );

  const goToPage = (page) => {
    const p = Math.max(1, Number(page) || 1);
    if (totalPages > 0 && p >= 1 && p <= totalPages && p !== currentPage) setCurrentPage(p);
  };

  const handleApplySearch = () => {
    setAppliedName(draftName);
    setAppliedCode(draftCode);
    setCurrentPage(1);
  };

  const handleResetSearch = () => {
    setDraftName('');
    setDraftCode('');
    setAppliedName('');
    setAppliedCode('');
    setCurrentPage(1);
  };

  const PaginationComponent = () => {
    if (totalPages <= 1) return null;
    const maxVisible = typeof window !== 'undefined' && window.innerWidth < 768 ? 3 : 5;
    let start = Math.max(1, safeCurrentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    const pages = Array.from({ length: end - start + 1 }, (_, i) => start + i);

    return (
      <div className="sticky bottom-0 z-20 flex flex-col items-center justify-between gap-2 border-t border-slate-200/90 bg-white/95 px-4 py-3 backdrop-blur-sm sm:flex-row">
        <div className="order-2 text-sm text-slate-500 sm:order-1">
          Affichage de {totalStores === 0 ? 0 : startIndex + 1} à {endIndex} sur {totalStores}
        </div>
        <div className="order-1 flex items-center gap-1 sm:order-2">
          <button
            type="button"
            onClick={() => goToPage(safeCurrentPage - 1)}
            disabled={safeCurrentPage === 1}
            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-600 transition hover:bg-slate-50 disabled:opacity-40"
          >
            <FaChevronLeft className="text-xs" />
          </button>
          {pages.map((p) => (
            <button
              type="button"
              key={p}
              onClick={() => goToPage(p)}
              className={`min-w-[2rem] rounded-lg border px-2.5 py-1.5 text-sm font-medium transition ${p === safeCurrentPage ? 'border-blue-600 bg-blue-600 text-white' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
            >
              {p}
            </button>
          ))}
          <button
            type="button"
            onClick={() => goToPage(safeCurrentPage + 1)}
            disabled={safeCurrentPage === totalPages}
            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-600 transition hover:bg-slate-50 disabled:opacity-40"
          >
            <FaChevronRight className="text-xs" />
          </button>
        </div>
      </div>
    );
  };

  // Resolve manager info for a store
  const resolveManager = (store) => {
    if (!store) return null;

    if (store.managerId && typeof store.managerId === 'object') {
      return store.managerId;
    }

    if (store.manager && typeof store.manager === 'object') {
      return store.manager;
    }

    const id = store.managerId ? String(store.managerId) : (store.manager ? String(store.manager) : null);
    if (!id) return null;
    const found = users.find(u => String(u._id || u.id) === id);
    return found || null;
  };

  const resolveMerchantName = (store) => {
    if (!store) return null;
    if (store.merchantName) return store.merchantName;

    const id = getStoreMerchantId(store);
    if (!id) return null;

    const fromMerchants = merchants.find((m) => String(m.id) === String(id));
    if (fromMerchants?.name) return fromMerchants.name;

    const merchantUser = users.find((u) => {
      if ((u.role || '').toLowerCase() !== 'merchant') return false;
      const mid = u.merchantId;
      const uid = mid && typeof mid === 'object' ? (mid.id || mid._id) : mid;
      return uid && String(uid) === String(id);
    });
    return merchantUser?.merchantName || null;
  };

  const getStoreMerchantId = (store) => {
    if (!store) return '';
    if (store.merchantId && typeof store.merchantId === 'object') return String(store.merchantId.id || store.merchantId._id || '');
    return store.merchantId ? String(store.merchantId) : '';
  };

  // format date
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('fr-FR', { year: 'numeric', month: '2-digit', day: '2-digit' }) + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  // Create store
  const handleCreate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const headers = authHeaders();
      if (!headers) { navigate('/login'); return; }
      const res = await fetch(`${STORE_API.replace(/\/$/, '')}/stores`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...newStore,
          merchantId: newStore.merchantId || undefined,
        })
      });
      if (!res.ok) await handleAuthError(res);
      const data = await res.json();
      const created = data.store || data;
      setStores((prev) => sortStoresByName([created, ...prev]));
      setCurrentPage(1);
      setNewStore({ name: '', code: '', address: { street: '', city: '', postalCode: '', country: 'Tunisie' }, phone: '', email: '', description: '', merchantId: '' });
      setShowForm(false);
      setStats(prev => ({ ...prev, total: prev.total + 1, active: created.active ? prev.active + 1 : prev.active }));
    } catch (err) {
      alert(`Erreur création : ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleActive = async (store) => {
    const id = store.id || store._id;
    const newActive = !store.active;
    setIsUpdatingId(id);
    try {
      const headers = authHeaders();
      if (!headers) { navigate('/login'); return; }
      const url = `${STORE_API.replace(/\/$/, '')}/stores/${id}/${newActive ? 'activate' : 'deactivate'}`;
      const res = await fetch(url, { method: 'PUT', headers });
      if (!res.ok) await handleAuthError(res);
      const data = await res.json();
      const updated = data.store || { ...store, active: newActive, updatedAt: new Date().toISOString() };
      setStores(prev => prev.map(s => (s.id === id || s._id === id) ? updated : s));
      setStats(prev => ({ total: prev.total, active: newActive ? prev.active + 1 : Math.max(0, prev.active - 1), inactive: newActive ? Math.max(0, prev.inactive - 1) : prev.inactive + 1 }));
    } catch (err) {
      alert(`Erreur : ${err.message}`);
    } finally {
      setIsUpdatingId(null);
    }
  };

  const openEditModal = (store) => {
    const id = store.id || store._id;
    setEditingStoreId(id);
    setEditStore({
      name: store.name || '',
      code: store.code || '',
      address: {
        street: store.address?.street || '',
        city: store.address?.city || '',
        postalCode: store.address?.postalCode || '',
        country: store.address?.country || 'Tunisie'
      },
      phone: store.phone || '',
      email: store.email || '',
      description: store.description || '',
      merchantId: getStoreMerchantId(store)
    });
    if (isAdmin) fetchMerchants();
    setShowEditForm(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editingStoreId) return;
    setIsEditSubmitting(true);
    try {
      const headers = authHeaders();
      if (!headers) { navigate('/login'); return; }

      const payload = {
        name: editStore.name,
        code: editStore.code,
        address: editStore.address,
        phone: editStore.phone,
        email: editStore.email,
        description: editStore.description,
      };

      if (isAdmin) {
        payload.merchantId = editStore.merchantId ? editStore.merchantId : null;
      }

      const res = await fetch(`${STORE_API.replace(/\/$/, '')}/stores/${editingStoreId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload)
      });

      const body = await res.json().catch(() => null);
      if (!res.ok) {
        if (res.status === 401) await handleAuthError(res);
        throw new Error(body?.message || `Erreur ${res.status}`);
      }

      const updated = body?.store || body;
      setStores((prev) => sortStoresByName(prev.map((s) => ((s.id || s._id) === editingStoreId ? updated : s))));
      setShowEditForm(false);
      setEditingStoreId(null);
    } catch (err) {
      alert(`Erreur modification : ${err.message}`);
    } finally {
      setIsEditSubmitting(false);
    }
  };

  // improved delete: display backend message and counts (utilisateurs/transactions) when 400
  const handleDelete = async (store) => {
    if (!window.confirm('Confirmez la suppression de ce magasin ?')) return;
    const id = store.id || store._id;
    setIsUpdatingId(id);
    try {
      const headers = authHeaders();
      if (!headers) { navigate('/login'); return; }
      const res = await fetch(`${STORE_API.replace(/\/$/, '')}/stores/${id}`, { method: 'DELETE', headers });

      // try to parse JSON body even on error
      const body = await res.json().catch(() => null);

      if (!res.ok) {
        // if backend returned 400 with details about associated users/transactions, show a friendly message
        if (res.status === 400 && body) {
          let message = body.message || 'Impossible de supprimer ce magasin';
          if (body.associatedUsers) message += `\nUtilisateurs associés : ${body.associatedUsers}`;
          if (body.associatedTransactions) message += `\nTransactions associées : ${body.associatedTransactions}`;
          alert(message);
          return;
        }
        // otherwise let handleAuthError either redirect or throw an error
        await handleAuthError(res);
      }

      // success -> remove from state
      setStores(prev => prev.filter(s => !(s.id === id || s._id === id)));
      setStats(prev => ({
        total: Math.max(0, prev.total - 1),
        active: store.active ? Math.max(0, prev.active - 1) : prev.active,
        inactive: store.active ? prev.inactive : Math.max(0, prev.inactive - 1)
      }));

    } catch (err) {
      alert(`Erreur suppression : ${err.message}`);
    } finally {
      setIsUpdatingId(null);
    }
  };

  const closeCreateForm = () => setShowForm(false);

  const closeEditForm = () => {
    if (isEditSubmitting) return;
    setShowEditForm(false);
  };

  const isFormView = showForm || showEditForm;

  const renderStoreFormContent = (store, setStore, merchantEmptyOption) => (
    <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2 lg:grid-cols-4">
      <StoreFormDivider label="Informations générales" />
      <StoreFormField label="Nom" required>
        <input required value={store.name} onChange={e => setStore(prev => ({ ...prev, name: e.target.value }))} placeholder="Ex: Mon Magasin" className={formInputClass} />
      </StoreFormField>
      <StoreFormField label="Code" required>
        <input required value={store.code} onChange={e => setStore(prev => ({ ...prev, code: e.target.value }))} placeholder="Ex: STORE001" className={`${formInputClass} font-mono tracking-wide`} />
      </StoreFormField>
      <StoreFormField label="Téléphone">
        <input placeholder="+216 …" value={store.phone} onChange={e => setStore(prev => ({ ...prev, phone: e.target.value }))} className={formInputClass} />
      </StoreFormField>
      <StoreFormField label="Email">
        <input type="email" placeholder="contact@exemple.tn" value={store.email} onChange={e => setStore(prev => ({ ...prev, email: e.target.value }))} className={formInputClass} />
      </StoreFormField>

      <StoreFormDivider label="Localisation" />
      <StoreFormField label="Rue" className="sm:col-span-2 lg:col-span-2">
        <input placeholder="Numéro et rue" value={store.address.street} onChange={e => setStore(prev => ({ ...prev, address: { ...prev.address, street: e.target.value } }))} className={formInputClass} />
      </StoreFormField>
      <StoreFormField label="Ville">
        <input placeholder="Ville" value={store.address.city} onChange={e => setStore(prev => ({ ...prev, address: { ...prev.address, city: e.target.value } }))} className={formInputClass} />
      </StoreFormField>
      <StoreFormField label="Code postal">
        <input placeholder="Code postal" value={store.address.postalCode} onChange={e => setStore(prev => ({ ...prev, address: { ...prev.address, postalCode: e.target.value } }))} className={formInputClass} />
      </StoreFormField>

      <StoreFormDivider label="Compléments" />
      <StoreFormField label="Description" className="sm:col-span-2 lg:col-span-4">
        <textarea value={store.description} onChange={e => setStore(prev => ({ ...prev, description: e.target.value }))} className={`${formInputClass} resize-none`} rows={2} placeholder="Notes ou description du point de vente" />
      </StoreFormField>

      {isAdmin && (
        <StoreFormField label="Marchand" className="sm:col-span-2 lg:col-span-2">
          {merchantsLoading ? (
            <div className="flex items-center gap-2 rounded-xl border border-slate-200/90 bg-slate-50/50 px-3.5 py-2.5 text-sm text-slate-500">
              <FaSpinner className="animate-spin text-blue-600" /> Chargement…
            </div>
          ) : (
            <select
              value={store.merchantId || ''}
              onChange={e => setStore(prev => ({ ...prev, merchantId: e.target.value }))}
              className={formInputClass}
            >
              <option value="">{merchantEmptyOption}</option>
              {merchants.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          )}
        </StoreFormField>
      )}
    </div>
  );

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
        <Navbar />

        {showForm && (
          <StoreFormShell
            title="Nouveau magasin"
            subtitle="Renseignez les informations du point de vente"
            formId="store-create-form"
            onSubmit={handleCreate}
            onBack={closeCreateForm}
            onCancel={closeCreateForm}
            submitting={isSubmitting}
            submitLabel="Créer"
            SubmitIcon={FaPlus}
          >
            {renderStoreFormContent(newStore, setNewStore, '— Aucun marchand —')}
          </StoreFormShell>
        )}

        {showEditForm && (
          <StoreFormShell
            title="Modifier magasin"
            subtitle={editStore.name || 'Mettre à jour les informations'}
            formId="store-edit-form"
            onSubmit={handleEdit}
            onBack={closeEditForm}
            onCancel={closeEditForm}
            submitting={isEditSubmitting}
            submitLabel="Enregistrer"
            SubmitIcon={FaEdit}
          >
            {renderStoreFormContent(editStore, setEditStore, '— Détacher le marchand —')}
          </StoreFormShell>
        )}

        {!isFormView && (
        <>
        <div className="bg-white border-b shadow-sm">
          <div className="p-3 sm:p-4 lg:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-3 sm:space-y-0">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg flex items-center justify-center"><FaStore className="text-white text-sm" /></div>
                <h1 className="text-lg sm:text-xl font-bold text-gray-900">Magasins</h1>
              </div>

              <div className="flex items-center space-x-2">
                <button onClick={() => { fetchStores(); fetchUsers(); if (isAdmin) fetchMerchants(); }} disabled={loading || usersLoading} className="flex items-center px-3 py-2 border rounded-lg text-xs font-medium hover:bg-gray-50">
                  <FaSync className={`${(loading || usersLoading) ? 'animate-spin' : 'group-hover:rotate-180'} mr-1.5 text-xs`} />
                  <span className="hidden sm:inline">Actualiser</span>
                </button>
                {canManageStores && (
                <button onClick={() => { setShowForm(true); fetchMerchants(); }} className="flex items-center px-3 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all text-xs font-medium shadow-lg hover:shadow-xl">
                  <FaPlus className="mr-1.5 text-xs" />
                  <span className="hidden sm:inline">Ajouter un magasin</span>
                </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
              <div className="flex min-w-0 items-center gap-2 rounded-xl border border-slate-200/80 bg-gradient-to-br from-white to-slate-50/90 px-2.5 py-2 shadow-sm ring-1 ring-slate-900/5">
                <FaStore className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                <div className="min-w-0">
                  <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-slate-500">Total</p>
                  <p className="text-sm font-bold tabular-nums tracking-tight text-slate-900">{stats.total}</p>
                </div>
              </div>
              <div className="flex min-w-0 items-center gap-2 rounded-xl border border-emerald-200/70 bg-emerald-50/50 px-2.5 py-2 shadow-sm ring-1 ring-emerald-900/5">
                <FaCircle className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                <div className="min-w-0">
                  <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-emerald-700/90">Actifs</p>
                  <p className="text-sm font-bold tabular-nums tracking-tight text-emerald-900">{stats.active}</p>
                </div>
              </div>
              <div className="col-span-2 flex min-w-0 items-center gap-2 rounded-xl border border-rose-200/70 bg-rose-50/50 px-2.5 py-2 shadow-sm ring-1 ring-rose-900/5 md:col-span-1">
                <FaCircle className="h-3.5 w-3.5 shrink-0 text-rose-600" />
                <div className="min-w-0">
                  <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-rose-700/90">Inactifs</p>
                  <p className="text-sm font-bold tabular-nums tracking-tight text-rose-900">{stats.inactive}</p>
                </div>
              </div>
            </div>

            <div className="mt-3 border-t border-slate-200/90 pt-3">
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Recherche</p>
              <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
                <div className="min-w-[120px] flex-1 sm:max-w-[180px]">
                  <label className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Nom</label>
                  <div className="relative mt-0.5">
                    <FaSearch className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={draftName}
                      onChange={(e) => setDraftName(e.target.value)}
                      placeholder="Fragment du nom…"
                      className="block w-full rounded-lg border border-slate-200 bg-white py-1.5 pl-7 pr-2 text-xs text-slate-800 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>
                </div>
                <div className="min-w-[100px] flex-1 sm:max-w-[140px]">
                  <label className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Code</label>
                  <input
                    type="text"
                    value={draftCode}
                    onChange={(e) => setDraftCode(e.target.value)}
                    placeholder="Ex. STORE001…"
                    className="mt-0.5 block w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-800 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    onClick={handleApplySearch}
                    disabled={loading}
                    className="inline-flex items-center rounded-lg bg-blue-600 px-2.5 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-50"
                  >
                    Appliquer
                  </button>
                  <button
                    type="button"
                    onClick={handleResetSearch}
                    disabled={loading}
                    className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
                  >
                    Réinitialiser
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <main className="flex-1 overflow-hidden p-3 sm:p-4 lg:p-6">
          {loading && (<div className="flex h-64 items-center justify-center px-4"><div className="text-center"><div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600"></div><p className="text-sm text-gray-600">Chargement des magasins...</p></div></div>)}

          {error && (<div className="m-0 rounded-xl border border-red-200 bg-red-50 p-4 text-center sm:p-6"><FaTimes className="mx-auto mb-2 text-2xl text-red-500" /><p className="text-sm font-medium text-red-700">Erreur : {error}</p></div>)}

          {!loading && !error && (
            <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm ring-1 ring-slate-900/5">
              <div className="hidden min-h-0 flex-1 lg:block">
                <div className="h-full overflow-auto">
                  <table className="min-w-full">
                    <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50">
                      <tr>
                        <th className="px-5 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">Nom</th>
                        <th className="px-4 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">Code</th>
                        <th className="px-4 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">Adresse</th>
                        <th className="px-4 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">Manager</th>
                        {isAdmin && <th className="px-4 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">Marchand</th>}
                        <th className="px-4 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">Statut</th>
                        {canManageStores && <th className="px-4 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">Actions</th>}
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-100 bg-white">
                      {filteredStores.length === 0 ? (
                        <tr>
                          <td colSpan={tableColSpan} className="px-6 py-14 text-center">
                            <div className="mx-auto flex max-w-sm flex-col items-center text-slate-500">
                              <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100">
                                <FaStore className="text-2xl text-slate-400" />
                              </div>
                              <p className="text-sm font-semibold text-slate-700">
                                {stores.length === 0 ? 'Aucun magasin trouvé' : 'Aucun résultat pour cette recherche'}
                              </p>
                              <p className="mt-1 text-xs text-slate-500">
                                {stores.length === 0 ? 'Commencez par ajouter votre premier magasin.' : 'Modifiez vos critères de recherche.'}
                              </p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        paginatedStores.map((store, idx) => {
                          const id = store.id || store._id;
                          const manager = resolveManager(store);
                          const merchantName = resolveMerchantName(store);
                          return (
                            <tr key={id} className={`transition-colors hover:bg-blue-50/40 ${idx % 2 === 1 ? 'bg-slate-50/40' : ''}`}>
                              <td className="px-5 py-4 align-top">
                                <div className="flex min-w-0 items-start gap-3">
                                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                                    <FaStore className="h-3.5 w-3.5" />
                                  </div>
                                  <div className="min-w-0">
                                    <div className="truncate text-sm font-semibold text-slate-900">{store.name}</div>
                                    <div className="mt-0.5 text-[11px] text-slate-500">Créé le {formatDate(store.createdAt)}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-4 align-top whitespace-nowrap">
                                <span className="inline-flex rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 font-mono text-xs font-semibold text-slate-700">
                                  {store.code}
                                </span>
                              </td>
                              <td className="px-4 py-4 align-top text-sm text-slate-600">
                                <div className="flex max-w-xs items-start gap-1.5 whitespace-normal break-words">
                                  <FaMapMarkerAlt className="mt-0.5 h-3 w-3 shrink-0 text-slate-400" />
                                  <span>{(store.address && (store.address.street || store.address.city)) ? `${store.address.street || ''} ${store.address.city || ''}`.trim() : '—'}</span>
                                </div>
                              </td>
                              <td className="px-4 py-4 align-top text-sm">
                                {manager ? (
                                  <div className="flex max-w-[15rem] items-start gap-2">
                                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                                      <FaUserTie className="h-3 w-3" />
                                    </div>
                                    <div className="min-w-0">
                                      <div className="truncate font-medium text-slate-900">{(manager.name || manager.firstName || manager.fullName || '') + (manager.lastName ? ` ${manager.lastName}` : '')}</div>
                                      {manager.email && <div className="truncate text-xs text-slate-500">{manager.email}</div>}
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-sm text-slate-400">Aucun</span>
                                )}
                              </td>

                              {isAdmin && (
                                <td className="px-4 py-4 align-top text-sm text-slate-700">
                                  <div className="max-w-[13rem] truncate">
                                    {merchantName || <span className="text-slate-400">—</span>}
                                  </div>
                                </td>
                              )}

                              <td className="px-4 py-4 align-top whitespace-nowrap">
                                {getStatusBadge(store.active)}
                              </td>

                              {canManageStores && (
                              <td className="px-4 py-4 align-top">
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() => openEditModal(store)}
                                    disabled={isUpdatingId === id}
                                    title="Modifier"
                                    aria-label="Modifier"
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-700 transition hover:bg-blue-100 disabled:opacity-50"
                                  >
                                    <FaEdit className="h-3.5 w-3.5" />
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => toggleActive(store)}
                                    disabled={isUpdatingId === id}
                                    title={store.active ? 'Désactiver' : 'Activer'}
                                    aria-label={store.active ? 'Désactiver' : 'Activer'}
                                    className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition disabled:opacity-50 ${store.active ? 'bg-amber-50 text-amber-700 hover:bg-amber-100' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}
                                  >
                                    {isUpdatingId === id ? <FaSpinner className="h-3.5 w-3.5 animate-spin" /> : store.active ? <FaToggleOff className="h-3.5 w-3.5" /> : <FaToggleOn className="h-3.5 w-3.5" />}
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => handleDelete(store)}
                                    disabled={isUpdatingId === id}
                                    title="Supprimer"
                                    aria-label="Supprimer"
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-rose-50 text-rose-700 transition hover:bg-rose-100 disabled:opacity-50"
                                  >
                                    {isUpdatingId === id ? <FaSpinner className="h-3.5 w-3.5 animate-spin" /> : <FaTrash className="h-3.5 w-3.5" />}
                                  </button>
                                </div>
                              </td>
                              )}
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* mobile */}
              <div className="min-h-0 flex-1 overflow-auto p-4 lg:hidden">
                {filteredStores.length === 0 ? (
                  <div className="py-12 text-center">
                    <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100">
                      <FaStore className="text-2xl text-slate-400" />
                    </div>
                    <p className="text-sm font-semibold text-slate-700">{stores.length === 0 ? 'Aucun magasin' : 'Aucun résultat'}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {paginatedStores.map(s => {
                      const id = s.id || s._id;
                      const manager = resolveManager(s);
                      return (
                        <div key={id} className="rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm ring-1 ring-slate-900/5 transition hover:shadow-md">
                          <div className="mb-3 flex items-start justify-between gap-3">
                            <div className="flex min-w-0 items-start gap-3">
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                                <FaStore className="h-4 w-4" />
                              </div>
                              <div className="min-w-0">
                                <div className="truncate text-sm font-semibold text-slate-900">{s.name}</div>
                                <div className="mt-1 inline-flex rounded-lg border border-slate-200 bg-slate-50 px-2 py-0.5 font-mono text-[11px] font-semibold text-slate-600">{s.code}</div>
                              </div>
                            </div>
                            <div className="shrink-0">{getStatusBadge(s.active)}</div>
                          </div>

                          <div className="mb-3 flex items-start gap-1.5 text-xs text-slate-600">
                            <FaMapMarkerAlt className="mt-0.5 h-3 w-3 shrink-0 text-slate-400" />
                            <span>{(s.address && (s.address.street || s.address.city)) ? `${s.address.street || ''} ${s.address.city || ''}`.trim() : '—'}</span>
                          </div>

                          <div className="mb-3 grid grid-cols-2 gap-3 text-xs">
                            <div>
                              <div className="mb-1 font-semibold uppercase tracking-wide text-slate-500">Manager</div>
                              {manager ? (<div className="text-sm font-medium text-slate-900">{(manager.name || manager.firstName || '') + (manager.lastName ? ` ${manager.lastName}` : '')}<div className="text-xs font-normal text-slate-500">{manager.email}</div></div>) : <div className="text-slate-400">Aucun</div>}
                            </div>
                            {isAdmin && (
                              <div>
                                <div className="mb-1 font-semibold uppercase tracking-wide text-slate-500">Marchand</div>
                                <div className="text-sm font-medium text-slate-900">{resolveMerchantName(s) || '—'}</div>
                              </div>
                            )}
                          </div>

                          {canManageStores && (
                          <div className="flex flex-col gap-3 border-t border-slate-100 pt-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="text-xs text-slate-500">Créé le {formatDate(s.createdAt)}</div>
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => openEditModal(s)}
                                title="Modifier"
                                aria-label="Modifier"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-700"
                              >
                                <FaEdit className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => toggleActive(s)}
                                title={s.active ? 'Désactiver' : 'Activer'}
                                aria-label={s.active ? 'Désactiver' : 'Activer'}
                                className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${s.active ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}
                              >
                                {s.active ? <FaToggleOff className="h-3.5 w-3.5" /> : <FaToggleOn className="h-3.5 w-3.5" />}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDelete(s)}
                                title="Supprimer"
                                aria-label="Supprimer"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-rose-50 text-rose-700"
                              >
                                <FaTrash className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                          )}
                          {!canManageStores && (
                            <div className="border-t border-slate-100 pt-3 text-xs text-slate-500">Créé le {formatDate(s.createdAt)}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <PaginationComponent />
            </div>
          )}
        </main>
        </>
        )}
      </div>
    </div>
  );
}
